import {
  addDays,
  differenceInCalendarDays,
  formatISO,
  parseISO,
} from 'date-fns'
import { content as contentV2 } from '@opallabs/api/v2'
import {
  content as contentV3,
  momentSchedules,
  moments,
} from '@opallabs/api/v3'

// A day can come back as midnight in the API's own zone. Only its date is read,
// so a reader in a zone west of that one does not move it to the day before.
export const shiftDay = (day: string, days: number): string =>
  formatISO(addDays(parseISO(day.slice(0, 10)), days), {
    representation: 'date',
  })

const shiftInstant = (instant: string, days: number): string =>
  addDays(parseISO(instant), days).toISOString()

/**
 * The end date to write for a moment that runs from `start` to `end`.
 *
 * A moment that ends the day it starts has no end date of its own, which is how
 * the API says a moment lasts a single day. The calendar draws both the same
 * way, so anything it is holding has to be read this way before it is written.
 */
export const endDateOf = ({
  end,
  start,
}: {
  end: string
  start: string
}): string | null => (end === start ? null : end)

/**
 * Moves a moment, and stretches it when its content has grown past the end.
 *
 * The schedule is read back before it is written so the dates on the wire are
 * the ones the API is holding, not the ones the calendar happens to be drawing.
 * That matters most for the end date: the calendar cannot tell a one-day moment
 * from a moment that ends the day it starts, and the API can.
 */
export const rescheduleMoment = async ({
  days,
  endDate,
  momentId,
}: {
  days: number
  /**
   * A new end date, for the stretch. Leave it out to only move the moment;
   * `null` gives the moment no end of its own, which is how the API says a
   * moment lasts a single day.
   */
  endDate?: string | null
  momentId: string
}) => {
  const document = await moments.readSingle({
    path: { moment_id: momentId },
    query: { include: ['schedules'] },
  })

  const scheduleId = document.data.relationships.schedules?.data?.[0]?.id
  if (scheduleId === undefined) {
    throw new Error(`Moment ${momentId} has no schedule to move.`)
  }

  const schedule = document.included?.find(
    (resource) =>
      resource.type === 'moment_schedule' && resource.id === scheduleId
  )
  if (schedule === undefined || !('start_at' in schedule.attributes)) {
    throw new Error(`Moment ${momentId} did not return its schedule.`)
  }

  const currentEnd = schedule.attributes.end_at
  const nextEnd =
    endDate === undefined
      ? currentEnd === null
        ? null
        : shiftDay(currentEnd, days)
      : endDate

  return momentSchedules.update({
    params: { path: { moment_schedule_id: scheduleId } },
    requestBody: {
      data: {
        attributes: {
          end_at: nextEnd,
          start_at: shiftDay(schedule.attributes.start_at, days),
        },
        id: scheduleId,
        type: 'moment_schedule',
      },
    },
  })
}

/**
 * Moves a piece of content, keeping the time of day it goes live and the length
 * of its in-market window.
 *
 * It takes two calls because no single endpoint can do it: `/content/v3` holds
 * the schedule but has no writable date, and only `/content/v2` takes an
 * absolute one — under an id that v3 hands out as `legacy_id`.
 */
export const rescheduleContent = async ({
  contentId,
  days,
  shouldClearTime = false,
}: {
  contentId: string
  days: number
  /**
   * Gives the content no time of its own, so it goes up when its moment
   * starts. This is how Undo puts back content that had no time before the
   * move: the move gave it one, and the API no longer knows it had none.
   */
  shouldClearTime?: boolean
}): Promise<{ hadScheduledTime: boolean }> => {
  const document = await contentV3.readSingle({
    path: { content_id: contentId },
    query: {},
  })

  const {
    has_own_takedown,
    has_scheduled_time,
    legacy_id,
    scheduled_at,
    takedown_scheduled_at,
  } = document.data.attributes
  if (scheduled_at === null) {
    throw new Error(`Content ${contentId} has no schedule to move.`)
  }

  await contentV2.update({
    params: { path: { content_id: legacy_id } },
    requestBody: {
      data: {
        attributes: {
          scheduled_at: shouldClearTime
            ? null
            : shiftInstant(scheduled_at, days),
          // A takedown that is not the content's own is its moment's end, and
          // moves with the moment. Writing it would make it the content's own.
          ...(has_own_takedown &&
            takedown_scheduled_at !== null && {
              takedown_scheduled_at: shiftInstant(takedown_scheduled_at, days),
            }),
        },
        id: legacy_id,
        relationships: {},
        type: 'content',
      },
    },
  })

  return { hadScheduledTime: has_scheduled_time }
}

/**
 * Puts the day a piece of content comes down on another date, keeping the time
 * of day it comes down at.
 *
 * Content with no takedown of its own is given one at the time of day it goes
 * up. The panel asks for a date and never a time, so the time has to come from
 * somewhere, and the time the content goes up is the only one it has.
 *
 * Both the day and the time of day are read in the reader's own zone, which is
 * the zone the month draws every time in: the API hands the calendar no zone of
 * its own. An hour the clocks change over is carried across with the day, so
 * the content still comes down at the time it says it does.
 */
export const rescheduleContentTakedown = async ({
  contentId,
  date,
}: {
  contentId: string
  /** `yyyy-MM-dd`, the day the content comes down. */
  date: string
}) => {
  const document = await contentV3.readSingle({
    path: { content_id: contentId },
    query: {},
  })

  const { has_own_takedown, legacy_id, scheduled_at, takedown_scheduled_at } =
    document.data.attributes
  // A takedown that is not the content's own is its moment's end, usually
  // midnight, which is no time the content was given.
  const from = (has_own_takedown ? takedown_scheduled_at : null) ?? scheduled_at
  if (from === null) {
    throw new Error(`Content ${contentId} has no schedule to come down off.`)
  }

  return contentV2.update({
    params: { path: { content_id: legacy_id } },
    requestBody: {
      data: {
        attributes: {
          takedown_scheduled_at: shiftInstant(
            from,
            differenceInCalendarDays(parseISO(date), parseISO(from))
          ),
        },
        id: legacy_id,
        relationships: {},
        type: 'content',
      },
    },
  })
}
